import React, { useMemo, useCallback } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  AlignItems,
  Box,
  Display,
  FontWeight,
  Icon,
  JustifyContent,
  withCitrusTheme,
  Text,
} from '@gousto-internal/citrus-react'
import { browserHistory } from 'react-router'
import { useLocation } from 'react-use'

import { client } from 'config/routes'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { CollectionSlug } from 'routes/Menu/domains/collections'
import { useIsNewMenuNavEnabled } from 'routes/Menu/hooks/useIsNewMenuNavEnabled'

const NEW_NAV_COLLECTIONS = [
  CollectionSlug.Recommendations,
  CollectionSlug.Browse,
  CollectionSlug.AllRecipes,
]

const BUTTON_CONFIG = {
  browse: {
    action: 'back_to_browse_button_clicked',
    label: 'Back',
    getPath: (pathname?: string) => ({
      pathname,
      search: '?collection=browse',
    }),
  },
  delivery: {
    action: 'back_to_delivery_details_button_clicked',
    label: 'Delivery',
    getPath: () => client.deliveryDetails,
  },
}

type BackButtonLinkProps = {
  label: string
  onClick: () => void
  onKeyDown: (event: React.KeyboardEvent) => void
}

type BackButtonWrapperProps = {
  variant: 'desktop' | 'mobile'
}

type UseBackButtonProps = {
  variant: 'desktop' | 'mobile'
}

const onSpaceOrEnter =
  (callback: (event: React.KeyboardEvent) => void) => (event: React.KeyboardEvent) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      callback(event)
    }
  }

export const useBackButton = ({ variant }: UseBackButtonProps) => {
  const { pathname } = useLocation()
  const currentCollectionId = useCurrentCollectionId()
  const currentCollection = useCollection(currentCollectionId)
  const isNewMenuNavEnabled = useIsNewMenuNavEnabled()

  const currentSlug = currentCollection?.slug ?? CollectionSlug.AllRecipes

  const shouldGoToBrowse =
    isNewMenuNavEnabled && variant === 'mobile' && !NEW_NAV_COLLECTIONS.includes(currentSlug)

  const buttonConfig = shouldGoToBrowse ? BUTTON_CONFIG.browse : BUTTON_CONFIG.delivery

  const onClick = useCallback(() => {
    datadogRum.addAction(buttonConfig.action)
    browserHistory.push(buttonConfig.getPath(pathname))
  }, [buttonConfig, pathname])

  return { label: buttonConfig.label, onClick }
}

const LinkButton = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

const BackButton = ({ label, onClick, onKeyDown }: BackButtonLinkProps) => {
  return (
    <LinkButton
      aria-label={`${label} Button`}
      data-testid="back-button-link"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
    >
      <Icon name="arrow_back" />
      <Text fontWeight={FontWeight.Bold} size={4}>
        {label}
      </Text>
    </LinkButton>
  )
}

export const BackButtonWrapper = ({ variant }: BackButtonWrapperProps) => {
  const { label, onClick } = useBackButton({ variant })
  const handleKeyDown = useMemo(() => onSpaceOrEnter(onClick), [onClick])

  return <BackButton label={label} onClick={onClick} onKeyDown={handleKeyDown} />
}
